// Pure helpers for cursor-paginated thread lists and the "stable list" rule:
// new arrivals never rearrange rows under the reader; they wait behind a "show new" pill.

export interface Keyed {
  key: string;
}

export interface Page<T> {
  threads: T[];
  nextCursor: string | null;
}

/** TanStack `getNextPageParam`: undefined means "no more pages". Guards against a cursor loop. */
export function nextCursor<T>(
  lastPage: Page<T>,
  allPages: readonly Page<T>[],
  lastPageParam?: string | null,
): string | undefined {
  const cursor = lastPage.nextCursor;
  if (!cursor) return undefined;
  if (lastPage.threads.length === 0) return undefined;
  if (cursor === lastPageParam) return undefined;
  const earlier = allPages.slice(0, -1);
  if (earlier.some((page) => page.nextCursor === cursor)) return undefined;
  return cursor;
}

/** Concatenate pages, keeping the first occurrence of each key (cursors can overlap after new mail). */
export function flattenPages<T extends Keyed>(pages: readonly Page<T>[] | undefined): T[] {
  if (!pages) return [];
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const item of page.threads) {
      if (seen.has(item.key)) continue;
      seen.add(item.key);
      out.push(item);
    }
  }
  return out;
}

export interface Reconciled<T> {
  /** Keys in display order (the new snapshot). */
  keys: string[];
  /** Items in display order; `gone` marks rows no longer in the fetched list. */
  items: (T & { gone?: boolean })[];
  /** Fetched items that arrived above/among the snapshot and are waiting to be shown. */
  pendingNew: number;
  /** True when applying the fetched order would change what the reader sees. */
  changed: boolean;
}

/**
 * Merge freshly fetched items into the snapshot the reader is looking at.
 * - rows already shown keep their position (their data updates in place);
 * - rows that disappeared stay as `gone` ghosts (using last known data) until the reader applies;
 * - items that come after the last shown row in fetched order (older pages) are appended;
 * - other unseen items are counted as `pendingNew`.
 */
export function reconcile<T extends Keyed>(
  snapshot: readonly string[] | null,
  fetched: readonly T[],
  lastKnown: ReadonlyMap<string, T>,
): Reconciled<T> {
  if (snapshot === null || snapshot.length === 0) {
    return { keys: fetched.map((t) => t.key), items: [...fetched], pendingNew: 0, changed: false };
  }
  const byKey = new Map(fetched.map((item, index) => [item.key, { item, index }]));
  const shown = new Set(snapshot);

  let lastShownIndex = -1;
  for (const key of snapshot) {
    const hit = byKey.get(key);
    if (hit && hit.index > lastShownIndex) lastShownIndex = hit.index;
  }

  const keys: string[] = [];
  const items: (T & { gone?: boolean })[] = [];
  let gone = 0;
  for (const key of snapshot) {
    const hit = byKey.get(key);
    if (hit) {
      keys.push(key);
      items.push(hit.item);
      continue;
    }
    const ghost = lastKnown.get(key);
    if (!ghost) continue;
    gone++;
    keys.push(key);
    items.push({ ...ghost, gone: true });
  }

  let pendingNew = 0;
  fetched.forEach((item, index) => {
    if (shown.has(item.key)) return;
    if (index > lastShownIndex) {
      keys.push(item.key);
      items.push(item);
    } else {
      pendingNew++;
    }
  });

  const presentInSnapshot = snapshot.filter((key) => byKey.has(key));
  const fetchedOrderOfShown = fetched.filter((t) => shown.has(t.key)).map((t) => t.key);
  const reordered = presentInSnapshot.some((key, i) => fetchedOrderOfShown[i] !== key);

  return { keys, items, pendingNew, changed: pendingNew > 0 || gone > 0 || reordered };
}

/** Move a selection index by `delta`, clamped to the list. Empty lists select nothing. */
export function moveSelection(current: number, delta: number, length: number): number {
  if (length <= 0) return -1;
  if (current < 0) return delta >= 0 ? 0 : length - 1;
  return Math.min(length - 1, Math.max(0, current + delta));
}

/** Whether the virtual window is close enough to the end to fetch the next page. */
export function shouldLoadMore(lastVisibleIndex: number, length: number, threshold = 10): boolean {
  return length > 0 && lastVisibleIndex >= length - threshold;
}
